<template>
  <!-- Backdrop for click-outside -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40"
    @click="$emit('close')"
  ></div>

  <div
    v-if="isOpen"
    ref="panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="release-notes-title"
    :class="[
      'fixed top-[52px] right-0 bottom-0 bg-white shadow-[0px_0px_100px_0px_rgba(0,0,0,0.2)] border border-[#E8E9E9] rounded-tl-lg z-50 flex flex-col overflow-hidden transition-all duration-200',
      isExpanded ? 'w-[900px]' : 'w-[534px]'
    ]"
  >
    <!-- Header -->
    <div class="px-10 py-5">
      <div class="flex items-start justify-between">
        <!-- Left side: Title -->
        <h2
          id="release-notes-title"
          class="text-xl font-semibold text-[#1E1F21] leading-none"
        >
          What's new
        </h2>

        <!-- Right side: Icons -->
        <div class="flex items-center gap-4">
          <button
            class="text-[#353537] hover:text-[#1E1F21] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4573D2] focus-visible:ring-offset-4 rounded"
            :title="isExpanded ? 'Collapse panel' : 'Expand panel'"
            @click="isExpanded = !isExpanded"
          >
            <font-awesome-icon :icon="isExpanded ? 'down-left-and-up-right-to-center' : 'up-right-and-down-left-from-center'" class="w-3.5 h-3.5" />
          </button>
          <button
            class="text-[#353537] hover:text-[#1E1F21] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4573D2] focus-visible:ring-offset-4 rounded"
            title="Close panel"
            @click="$emit('close')"
          >
            <font-awesome-icon icon="arrow-right-from-bracket" class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div class="w-full h-px bg-[#E8E9E9]"></div>

    <!-- Feed -->
    <div
      tabindex="0"
      role="region"
      aria-label="Release notes feed"
      class="flex-1 overflow-y-auto pb-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4573D2] focus-visible:ring-inset"
    >
      <!-- Manifest loading -->
      <LoadingIndicator v-if="isManifestLoading" message="Loading release notes..." />

      <!-- Manifest error -->
      <div v-else-if="manifestError" class="flex items-center justify-center h-32 px-10">
        <p class="text-[#D53D3D] text-sm">{{ manifestError }}</p>
      </div>

      <!-- Manifest empty -->
      <div v-else-if="!releases.length" class="flex flex-col items-center justify-center h-32 px-10 text-center">
        <p class="text-sm font-semibold text-[#1E1F21]">No release notes yet</p>
        <p class="mt-2 text-sm text-[#626264]">
          Release notes will appear here as they are published.
        </p>
      </div>

      <template v-else>
        <section
          v-for="(release, index) in visibleReleases"
          :key="release.date"
        >
          <!-- Sticky dated section header -->
          <h3
            class="sticky top-0 z-10 bg-white border-b border-[#E8E9E9] px-10 py-2 flex items-center gap-2 text-[13px] font-semibold text-[#1E1F21]"
          >
            {{ release.title }}
            <span
              v-if="isUnseen(release)"
              class="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700"
            >
              New
            </span>
          </h3>

          <div class="px-10 pt-4 pb-5">
            <template v-if="isRevealed(index)">
              <!-- This release failed; the rest of the feed still renders -->
              <p v-if="errorCache[release.date]" class="text-[#D53D3D] text-sm">
                {{ errorCache[release.date] }}
              </p>
              <div
                v-else
                class="prose prose-sm max-w-none prose-headings:text-[#1E1F21] prose-h1:hidden prose-h2:text-base prose-h2:font-semibold prose-h2:mt-5 prose-h2:mb-2 first:prose-h2:mt-0 prose-h3:text-sm prose-h3:font-medium prose-h3:mt-4 prose-h3:mb-2 prose-p:text-[#353537] prose-p:text-sm prose-ul:my-2 prose-ul:text-sm prose-li:my-0.5 prose-li:text-[#353537]"
                v-html="contentCache[release.date]"
              ></div>
            </template>
            <LoadingIndicator v-else message="" />
          </div>
        </section>

        <!-- Show more -->
        <div v-if="hasMore" class="px-10 pt-4 flex justify-center">
          <button
            class="btn-secondary-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4573D2] focus-visible:ring-offset-4"
            @click="loadMore"
          >
            Show more
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

// Releases rendered on open, and how many each "Show more" appends.
const INITIAL_COUNT = 3
const PAGE_SIZE = 5
// Newest release date the user has already seen, written on panel close.
const LAST_SEEN_KEY = 'releaseNotes.lastSeenDate'
// There is no global axios timeout, so a hung request never rejects. The feed
// reveals in order, which means a hung fetch for the newest release would stall
// every section below it indefinitely. Bounding it turns a hang into an ordinary
// error: that section says so inline and the rest of the feed carries on.
// Generous for files of ~3.5KB.
const FETCH_TIMEOUT_MS = 10000

export default {
  name: 'ReleaseNotesPanel',
  components: { LoadingIndicator },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      releases: [],
      // Sanitized HTML by date. Only ever holds successfully loaded content —
      // failures live in errorCache so a failed fetch cannot render as body copy.
      contentCache: {},
      errorCache: {},
      visibleCount: 0,
      isExpanded: false,
      isManifestLoading: false,
      manifestError: null,
      // Snapshotted on open so the "New" pills stay put while the panel is read.
      lastSeenDate: '',
    }
  },
  computed: {
    newestRelease() {
      return this.releases[0]
    },
    visibleReleases() {
      return this.releases.slice(0, this.visibleCount)
    },
    hasMore() {
      return this.releases.length > this.visibleCount
    },
    // How many releases from the top have settled with no gap. The fetches run in
    // parallel and land in whatever order, and the older files are usually the
    // smaller ones, so without this the feed filled bottom-up and the newest
    // release — the one the reader is looking at — popped in last and shoved
    // everything down. Revealing only the settled prefix makes the feed fill
    // strictly top-down: content never appears above content already on screen.
    settledPrefix() {
      const i = this.visibleReleases.findIndex(r => !this.isSettled(r.date))
      return i === -1 ? this.visibleReleases.length : i
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.lastSeenDate = this.readLastSeenDate()
        this.fetchManifest()
        document.addEventListener('keydown', this.handleKeydown)
      } else {
        this.markSeen()
        document.removeEventListener('keydown', this.handleKeydown)
      }
    },
  },
  created() {
    // Dates with a request in flight. Not reactive — nothing renders off it, and
    // it must be read synchronously by concurrent fetchContent calls.
    this.inFlight = new Set()
  },
  beforeUnmount() {
    if (this.isOpen) this.markSeen()
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('close')
      }
    },
    readLastSeenDate() {
      try {
        return localStorage.getItem(LAST_SEEN_KEY) || ''
      } catch {
        return ''
      }
    },
    markSeen() {
      if (!this.newestRelease) return
      try {
        localStorage.setItem(LAST_SEEN_KEY, this.newestRelease.date)
      } catch {
        // Storage unavailable (private mode, quota) — the pill is a nicety, not a feature.
      }
    },
    isUnseen(release) {
      // No stored value means a first-ever visit; flagging all 33 as new is noise.
      if (!this.lastSeenDate) return false
      // Dates are ISO (YYYY-MM-DD), so lexicographic order is chronological.
      return release.date > this.lastSeenDate
    },
    async fetchManifest() {
      this.manifestError = null
      this.isManifestLoading = true
      try {
        const response = await this.$axios.get('/api/document-library/download/', {
          timeout: FETCH_TIMEOUT_MS,
          params: { key: 'GENERAL/release-notes/manifest.json', content: 'true' },
        })
        const manifest = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
        this.releases = manifest.releases || []
        this.visibleCount = Math.min(INITIAL_COUNT, this.releases.length)
        this.loadVisibleContent()
      } catch (e) {
        console.error('Failed to load release notes manifest:', e)
        this.manifestError = 'Failed to load release notes'
      }
      this.isManifestLoading = false
    },
    loadVisibleContent() {
      return Promise.all(this.visibleReleases.map(release => this.fetchContent(release.date)))
    },
    loadMore() {
      this.visibleCount = Math.min(this.visibleCount + PAGE_SIZE, this.releases.length)
      this.loadVisibleContent()
    },
    isSettled(date) {
      return this.isLoaded(date) || date in this.errorCache
    },
    isRevealed(index) {
      return index < this.settledPrefix
    },
    isLoaded(date) {
      // Presence, not truthiness: an empty release file renders as '' and is still
      // loaded. Testing truthiness would pin that section on a spinner forever and
      // refetch it on every "Show more".
      return date in this.contentCache
    },
    async fetchContent(date) {
      // Guard on both the completed and the in-flight set. loadVisibleContent()
      // re-maps the whole visible list, so without the latter a "Show more" click
      // landing before the initial three resolve would re-issue all three.
      if (this.isLoaded(date) || this.inFlight.has(date)) return
      this.inFlight.add(date)
      delete this.errorCache[date]
      try {
        const response = await this.$axios.get('/api/document-library/download/', {
          // The backend serves .md as text/plain. The global axios default of
          // responseType: 'json' (main.js) would hard-throw on the markdown body,
          // so text-returning call sites must opt out per call.
          responseType: 'text',
          timeout: FETCH_TIMEOUT_MS,
          params: { key: `GENERAL/release-notes/${date}.md`, content: 'true' },
        })
        this.contentCache[date] = DOMPurify.sanitize(marked(response.data))
      } catch (e) {
        console.error('Failed to load release notes content:', e)
        this.errorCache[date] = 'Failed to load this release.'
      } finally {
        this.inFlight.delete(date)
      }
    },
  },
}
</script>
